import { Labeled } from '@components/form/Labeled'
import type {
  ProrationBase,
  SettlementDetail
} from '@services/settlements/types'
import { useState } from 'react'
import { Controller, type UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button } from '@/components/ui/button'
import { Combobox } from '@/components/ui/combobox'
import { Input } from '@/components/ui/input'

import { formatMonths, months, periodMonths } from '../format'
import { DateField, MoneyField, StepIntro } from './fields'
import type { SettlementFormValues } from './useSettlementInputsForm'

const BASES: ProrationBase[] = ['DAYS', 'HALF_MONTHS', 'FULL_MONTHS']

export function StepPeriod({
  form,
  settlement
}: {
  form: UseFormReturn<SettlementFormValues>
  settlement: SettlementDetail
}) {
  const { t } = useTranslation('settlement')
  const [advanced, setAdvanced] = useState(false)

  const [start, end, costsStart, costsEnd, base, override] = form.watch([
    'period_start',
    'period_end',
    'costs_period_start',
    'costs_period_end',
    'proration_base',
    'period_months_override'
  ])

  /**
   * Worked out here rather than read off the last server response, so the
   * number moves as the dates are typed instead of after a save.
   */
  const derived = periodMonths(start, end, base, costsStart, costsEnd)
  const computedMonths =
    derived === null
      ? months(settlement.result.proration_share) ?? '—'
      : formatMonths(derived)
  const effectiveMonths = override || computedMonths

  return (
    <div>
      <StepIntro>{t('wizard.period.intro')}</StepIntro>

      <div className="grid gap-4 sm:grid-cols-2">
        <Labeled label={t('fields.title')} hint={t('hints.title')}>
          <Input {...form.register('title')} />
        </Labeled>
        <div />

        <DateField
          name="period_start"
          label={t('fields.period_start')}
          hint={t('hints.period_start')}
          required
          control={form.control}
        />
        <DateField
          name="period_end"
          label={t('fields.period_end')}
          hint={t('hints.period_end')}
          required
          control={form.control}
        />

        <DateField
          name="costs_period_start"
          label={t('fields.costs_period_start')}
          hint={t('hints.costs_period')}
          required
          control={form.control}
        />
        <DateField
          name="costs_period_end"
          label={t('fields.costs_period_end')}
          required
          control={form.control}
        />

        {/* The computed value sits in the placeholder, so an empty field
            reads as the number it will actually use. */}
        <MoneyField
          name="period_months_override"
          label={t('fields.period_months')}
          hint={t('hints.period_months', { months: computedMonths })}
          suffix={t('result.period_days_unit')}
          placeholder={computedMonths}
          narrow
          register={form.register}
        />
      </div>

      {/* The arithmetic every annual amount goes through, spelled out. */}
      <p className="m-0 mt-4 rounded-xl bg-ink/[0.03] px-4 py-3 text-sm text-ink">
        {t('fields.period_formula', { months: effectiveMonths })}
      </p>

      <div className="mt-5">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={() => setAdvanced((open) => !open)}
        >
          {advanced ? '−' : '+'} {t('sections.advanced')}
        </Button>

        {advanced && (
          <div className="mt-3 grid gap-4 sm:grid-cols-2">
            <Labeled
              label={t('fields.proration_base')}
              hint={t('hints.proration_base')}
            >
              <Controller
                control={form.control}
                name="proration_base"
                render={({ field }) => (
                  <Combobox
                    value={field.value}
                    searchable={false}
                    options={BASES.map((value) => ({
                      value,
                      label: t(`proration.${value}`)
                    }))}
                    onChange={(value) =>
                      field.onChange((value as ProrationBase) ?? 'DAYS')
                    }
                  />
                )}
              />
            </Labeled>
          </div>
        )}
      </div>
    </div>
  )
}
